
import { Component, Vue, Watch } from "vue-property-decorator";
import Moment from "moment";
import httpClient from "../../store/httpClient";
import { OrderTrackingDto } from "@/dtos/orderTrackingDto";
import { OrderTrackingPositionDto } from "@/dtos/orderTrackingPositionDto";
import { CreditNoteDto } from "@/dtos/creditNoteDto";
import { OrderTrackingState } from "@/dtos/orderTrackingState";
import { TicketStateDto } from "@/dtos/ticket/ticketStateDto";
import TicketComponent from "@/views/OrderHistory/TicketComponent.vue";
import rules from "@/store/rules";
import { AddressDto } from "@/dtos/addressDto";
import { AddressType } from "@/dtos/addressType";
import ChangeAddress from "@/views/Settings/ChangeAddress.vue";

type ArchiveFilter = "all" | "orders" | "credits";

@Component({
	components: {
		TicketComponent,
		ChangeAddress,
	},
})
export default class Orders extends Vue {
	private ticketStates: TicketStateDto[] = new Array<TicketStateDto>();
	private loadBatchSize: number = 10;
	private hasMoreToLoad: boolean = false;
	private isLoadingPdf: boolean = false;
	private isLoadingPrepayPdf: boolean = false;
	private isLoadingOrderPdf: boolean = false;
	private isLoadingCancellation: boolean = false;
	private loadingTicketStates: boolean = true;
	private optionalStringMax35Rule = rules.optionalStringMax35Rule;
	private showCancellationConfirmationDialog: boolean = false;
	private orderIdToCancel: number = 0;
	private currentEditAddress: AddressDto = new AddressDto();
	private currentEditAddressType: AddressType = AddressType.CompletedOrderDelivery;
	private currentEditAddressOrderId: number = 0;
	private editAddressDialog: boolean = false;

	// Gutschriften und Filterung
	private creditNotes: CreditNoteDto[] = new Array<CreditNoteDto>();
	private isLoadingCreditNotes: boolean = false;
	private isLoadingCreditNotePdf: boolean = false;
	private activeFilter: ArchiveFilter = "all";
	private searchTerm: string = "";
	private searchDebounceTimer: number | null = null;

	beforeRouteEnter(to: any, from: any, next: any) {
		next((vm: any) => {
			window.addEventListener("scroll", vm.checkOrdersBottom);
		});
	}

	beforeRouteLeave(to: any, from: any, next: any) {
		window.removeEventListener("scroll", this.checkOrdersBottom);
		next();
	}

	private mounted() {
		this.loadOrders(true);
		this.loadCreditNotes();
	}

	// Bestellungen werden serverseitig gesucht: Bei Änderung des Suchbegriffs
	// werden sie (entprellt) neu ab dem ersten Treffer geladen. Gutschriften
	// werden lokal gefiltert (siehe matchesCreditSearch).
	@Watch("searchTerm")
	private onSearchTermChanged() {
		if (this.searchDebounceTimer !== null) {
			clearTimeout(this.searchDebounceTimer);
		}
		this.searchDebounceTimer = window.setTimeout(() => {
			this.loadOrders(true);
		}, 400);
	}

	private get orders() {
		return this.$store.state.orders as OrderTrackingDto[];
	}

	private get orderCancellationRequests() {
		return this.$store.state.orderCancellations;
	}

	private get isLoading() {
		return this.$store.state.loading as boolean;
	}

	private get showOrders() {
		return this.activeFilter !== "credits";
	}

	private get showCredits() {
		return this.activeFilter !== "orders";
	}

	private get visibleOrders(): OrderTrackingDto[] {
		if (!this.showOrders) {
			return [];
		}
		// Die Suche nach Bestellungen erfolgt serverseitig (siehe loadOrders).
		return this.orders;
	}

	private get matchingCreditNotes(): CreditNoteDto[] {
		// Nach Suchbegriff gefiltert, aber unabhängig vom aktiven Typ-Filter,
		// damit der Zähler im Segment-Filter immer die gefundene Anzahl zeigt.
		return this.creditNotes.filter((c) => this.matchesCreditSearch(c));
	}

	private get visibleCreditNotes(): CreditNoteDto[] {
		if (!this.showCredits) {
			return [];
		}
		return this.matchingCreditNotes;
	}

	/**
	 * Bestellungen und Gutschriften zu einer chronologisch sortierten Liste
	 * (neuste zuerst) verschränkt. Nutzt das OrderDate der Bestellung bzw.
	 * das CreatedAt der Gutschrift.
	 */
	private get archiveEntries(): {
		type: "order" | "credit";
		date: number;
		order?: OrderTrackingDto;
		credit?: CreditNoteDto;
	}[] {
		const entries: {
			type: "order" | "credit";
			date: number;
			order?: OrderTrackingDto;
			credit?: CreditNoteDto;
		}[] = [];
		this.visibleOrders.forEach((o) =>
			entries.push({ type: "order", date: this.orderTimestamp(o), order: o })
		);
		this.visibleCreditNotes.forEach((c) =>
			entries.push({ type: "credit", date: this.creditTimestamp(c), credit: c })
		);
		return entries.sort((a, b) => b.date - a.date);
	}

	private orderTitle(order: OrderTrackingDto): string {
		const date = order.orderDate
			? Moment(String(order.orderDate)).format("DD.MM.YYYY")
			: "";
		return this.$t("orderHistory.orders.orderTitle", {
			number: order.orderId,
			date,
		}) as string;
	}

	private orderTimestamp(order: OrderTrackingDto): number {
		// Fallback auf dueDate, falls das Backend noch kein orderDate liefert.
		const date = order.orderDate || order.dueDate;
		return date ? new Date(date).getTime() : 0;
	}

	private creditTimestamp(cn: CreditNoteDto): number {
		return cn.createdAt ? new Date(cn.createdAt).getTime() : 0;
	}

	private get openCreditTotal(): number {
		return this.creditNotes
			.filter((c) => this.isOpenCredit(c))
			.reduce((sum, c) => sum + c.amount, 0);
	}

	private matchesCreditSearch(cn: CreditNoteDto): boolean {
		const term = this.searchTerm.trim().toLowerCase();
		if (!term) {
			return true;
		}
		return (
			("gs-" + cn.id).includes(term) ||
			cn.id.toString().includes(term) ||
			(cn.reason || "").toLowerCase().includes(term) ||
			(cn.createdFromCustomerOrderId != null &&
				cn.createdFromCustomerOrderId.toString().includes(term))
		);
	}

	// --- Gutschriften ---

	private async loadCreditNotes() {
		try {
			this.isLoadingCreditNotes = true;
			const response = await httpClient().get("creditnote/GetCreditNotesOfCustomer");
			this.creditNotes = response.data;
		} catch (err) {
			console.log(`Fehler beim Laden der Gutschriften: ${err}`);
		} finally {
			this.isLoadingCreditNotes = false;
		}
	}

	private async getCreditNotePdf(creditNoteId: number) {
		try {
			this.isLoadingCreditNotePdf = true;
			const response = await httpClient().get(
				`creditnote/GetCreditNotePdf?creditNoteId=${creditNoteId}`,
				{
					responseType: "blob",
				}
			);
			const url = URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement("a");
			link.href = url;
			link.download = this.$t("orderHistory.orders.creditNote.fileName", {
				creditNoteId,
			}) as string;
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			console.error(`Get credit note pdf failed. ${err}`);
		} finally {
			this.isLoadingCreditNotePdf = false;
		}
	}

	private isOpenCredit(cn: CreditNoteDto): boolean {
		return !cn.deletedAt && (cn.invoiceId === null || cn.invoiceId === undefined);
	}

	private creditStateLabel(cn: CreditNoteDto): string {
		if (cn.deletedAt) {
			return this.$t("orderHistory.orders.creditNote.stateRefunded") as string;
		}
		if (cn.invoiceId != null) {
			return this.$t("orderHistory.orders.creditNote.stateSettled") as string;
		}
		return this.$t("orderHistory.orders.creditNote.stateOpen") as string;
	}

	private creditChipClass(cn: CreditNoteDto): string {
		return this.isOpenCredit(cn) ? "open" : "settled";
	}

	private creditRailColor(cn: CreditNoteDto): string {
		return this.isOpenCredit(cn) ? "#2f855a" : "#8b929a";
	}

	// --- Status der Bestellungen ---

	// Kreditlimit überschritten, aber Vorausrechnung bereits versendet:
	// Der Kunde muss diese begleichen.
	private isWaitingForPayment(order: OrderTrackingDto): boolean {
		return order.state === OrderTrackingState.CreditOverdrawn && order.prepayInvoiceId != null;
	}

	private stateLabel(order: OrderTrackingDto): string {
		if (this.isWaitingForPayment(order)) {
			return "Warten auf Zahlungseingang";
		}
		switch (order.state) {
			case OrderTrackingState.Ordered:
				return this.$t("orderHistory.orders.states.ordered") as string;
			case OrderTrackingState.Delivered:
				return this.$t("orderHistory.orders.states.delivered") as string;
			case OrderTrackingState.Paid:
				return this.$t("orderHistory.orders.states.paid") as string;
			case OrderTrackingState.InProduction:
				return this.$t("orderHistory.orders.states.inProduction") as string;
			case OrderTrackingState.Produced:
				return this.$t("orderHistory.orders.states.produced") as string;
			case OrderTrackingState.Fakturiert:
				return this.$t("orderHistory.orders.states.invoiced") as string;
			case OrderTrackingState.Cancelled:
				return this.$t("orderHistory.orders.states.cancelled") as string;
			case OrderTrackingState.CreditOverdrawn:
				return this.$t("orderHistory.orders.states.creditOverdrawn") as string;
			default:
				return this.$t("orderHistory.orders.states.unknown") as string;
		}
	}

	private railColor(order: OrderTrackingDto): string {
		switch (this.chipClass(order)) {
			case "progress":
				return "#efbb20";
			case "transit":
				return "#2a72b5";
			case "done":
				return "#3f8f5b";
			case "alert":
				return "#c0392b";
			default:
				return "#8b929a";
		}
	}

	private chipClass(order: OrderTrackingDto): string {
		if (this.isWaitingForPayment(order)) {
			return "progress";
		}
		switch (order.state) {
			case OrderTrackingState.Ordered:
			case OrderTrackingState.InProduction:
				return "progress";
			case OrderTrackingState.Delivered:
			case OrderTrackingState.Produced:
			case OrderTrackingState.Fakturiert:
				return "transit";
			case OrderTrackingState.Paid:
				return "done";
			case OrderTrackingState.Cancelled:
			case OrderTrackingState.CreditOverdrawn:
				return "alert";
			default:
				return "settled";
		}
	}

	private certificatesSuffix(order: OrderTrackingDto): string {
		return order.hasCertificatesRequested &&
			(order.state === 2 || order.state === 3 || order.state === 6)
			? (this.$t("orderHistory.orders.certificatesSuffix") as string)
			: "";
	}

	private openDetail(order: OrderTrackingDto) {
		this.$router.push({
			name: "OrderDetail",
			params: { orderId: order.orderId.toString() },
		});
	}

	// --- Artikel-Vorschaubilder / Anzahl ---

	private orderPositions(order: OrderTrackingDto): OrderTrackingPositionDto[] {
		return order.positions || [];
	}

	private articleCount(order: OrderTrackingDto): number {
		return this.orderPositions(order).length;
	}

	private thumbPositions(order: OrderTrackingDto): OrderTrackingPositionDto[] {
		return this.orderPositions(order)
			.filter((p) => p.articleId != null)
			.slice(0, 5);
	}

	private extraThumbCount(order: OrderTrackingDto): number {
		const rest = this.articleCount(order) - this.thumbPositions(order).length;
		return rest > 0 ? rest : 0;
	}

	private thumbnailUrl(articleId: number | null): string {
		return this.$store.getters.getThumbnailUrl(articleId, undefined);
	}

	private hideBrokenThumb(event: Event) {
		const img = event.target as HTMLElement;
		if (img) {
			img.style.display = "none";
		}
	}

	// --- Bestehende Bestell-Logik (unverändert übernommen) ---

	private async loadOrders(reload: boolean) {
		const previousCount = this.orders.length;
		try {
			this.$store.commit("setLoading", true);
			var from = reload ? 0 : previousCount;
			const response = await httpClient().get(
				`order/getOrdersOfCustomer?from=${from}&take=${this.loadBatchSize}` +
					`&searchTerm=${encodeURIComponent(this.searchTerm.trim())}`
			);
			this.$store.commit("setOrders", {
				reload: reload || from === 0,
				orders: response.data,
			});
			if (reload) {
				this.hasMoreToLoad = response.data.length === this.loadBatchSize;
			} else {
				// Nur weiter-nachladen, wenn ein voller Batch kam UND tatsächlich
				// neue Bestellungen dazukamen. Verhindert Endlos-Nachladen am Ende
				// (leere oder doppelte Antworten wachsen den Store nicht).
				this.hasMoreToLoad =
					response.data.length === this.loadBatchSize &&
					this.orders.length > previousCount;
			}
		} catch (err) {
			console.log(`Fehler beim Laden der Bestellungen: ${err}`);
			// Bei einem Fehler nicht endlos weiter automatisch nachladen.
			if (!reload) {
				this.hasMoreToLoad = false;
			}
		} finally {
			this.$store.commit("setLoading", false);
		}
		// Load cancellations after order have been loaded
		this.loadOrderCancellationRequests();
		// Load Ticket States after orders have been loaded
		this.getTicketStates();
	}

	private async loadOrderCancellationRequests() {
		try {
			const response = await httpClient().post(
				"order/GetOrderCancellationRequestsForOrders",
				this.orders.map((o: OrderTrackingDto) => o.orderId)
			);
			this.$store.commit("setOrderCancellations", response.data);
		} catch (err) {
			console.log(`Fehler beim Laden der Stornierungsanfragen: ${err}`);
		}
	}

	private async getTicketStates() {
		try {
			this.loadingTicketStates = true;
			const response = await httpClient().get("Order/GetTicketStates");
			if (response.data !== "") {
				if (response.data.length === 0) {
					this.ticketStates = new Array<TicketStateDto>();
				} else {
					this.ticketStates = response.data;
				}
			}
		} catch (err) {
			console.log(err);
		} finally {
			this.loadingTicketStates = false;
		}
	}

	private getTicketStateFromOrderId(order: OrderTrackingDto) {
		var returnStates = this.ticketStates.filter((state) => {
			return state.orderId === order.orderId.toString();
		});
		if (returnStates.length > 0) {
			return returnStates[0];
		} else {
			return undefined;
		}
	}

	private hasCancellationRequest(orderId: number) {
		return (
			this.orderCancellationRequests !== null &&
			this.orderCancellationRequests.some((o: any) => o.blexonOrderId == orderId)
		);
	}

	private hasWaitingCancellationRequest(orderId: number) {
		if (this.hasCancellationRequest(orderId)) {
			return (
				this.orderCancellationRequests.filter((o: any) => o.blexonOrderId == orderId)[0]
					.state == 0
			);
		} else {
			return false;
		}
	}

	private hasOpenCancellationRequest(orderId: number) {
		if (this.hasCancellationRequest(orderId)) {
			return (
				this.orderCancellationRequests.filter((o: any) => o.blexonOrderId == orderId)[0]
					.state == 1
			);
		} else {
			return false;
		}
	}

	private hasClosedCancellationRequest(orderId: number) {
		if (this.hasCancellationRequest(orderId)) {
			let state = this.orderCancellationRequests.filter(
				(o: any) => o.blexonOrderId == orderId
			)[0].state;
			return state == 2 || state == 3;
		} else {
			return false;
		}
	}

	private hasBusyCancellationRequest(orderId: number) {
		if (this.hasCancellationRequest(orderId)) {
			let state = this.orderCancellationRequests.filter(
				(o: any) => o.blexonOrderId == orderId
			)[0].state;
			return state == 4;
		} else {
			return false;
		}
	}

	private canChangeKommissionsnummer(order: OrderTrackingDto) {
		return (
			order.state == OrderTrackingState.Ordered ||
			order.state == OrderTrackingState.InProduction ||
			order.state == OrderTrackingState.Produced
		);
	}

	private canChangeDeliveryAddress(order: OrderTrackingDto) {
		return (
			(order.state == OrderTrackingState.Ordered ||
				order.state == OrderTrackingState.InProduction) &&
			["Schweizer Post", "DHL", "Noch offen"].some(
				(t: string) => t === order.translatedZustellungArt
			)
		);
	}
	private canChangeInvoiceAddress(order: OrderTrackingDto) {
		return (
			order.state == OrderTrackingState.Ordered ||
			order.state == OrderTrackingState.InProduction ||
			order.state == OrderTrackingState.Delivered ||
			order.state == OrderTrackingState.Produced
		);
	}

	private async updateKommissionsnummer(order: OrderTrackingDto) {
		if (order.kommission === null || order.kommission === "") {
			return;
		}
		try {
			await httpClient().post(
				"order/updateKommissionsnummer?" +
					`newKommissionsnummer=${encodeURI(order.kommission)}` +
					`&customerOrderId=${order.orderId}`
			);
			this.$store.dispatch(
				"setSnackbarText",
				this.$t("orderHistory.orders.messages.kommissionSaved")
			);
		} catch (err) {
			console.log(`Fehler beim Aktualisieren der Auftragsnummer: ${err}`);
			this.$store.dispatch(
				"setSnackbarErrorText",
				this.$t("orderHistory.orders.messages.kommissionSaveError")
			);
		}
	}

	private async getInvoicePdf(orderId: number) {
		try {
			this.isLoadingPdf = true;
			const response = await httpClient().get(
				`order/getInvoicePdf?customerOrderId=${orderId}`,
				{
					responseType: "blob",
				}
			);
			const url = URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement("a");
			link.href = url;
			link.download = this.$t("orderHistory.orders.invoice.fileName", { orderId }) as string;
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			console.log(`Get invoice failed. ${err}`);
		} finally {
			this.isLoadingPdf = false;
		}
	}

	private async getPrepayInvoicePdf(invoiceId: number) {
		try {
			this.isLoadingPrepayPdf = true;
			const response = await httpClient().get(
				`order/getPrepayInvoicePdf?invoiceId=${invoiceId}`,
				{
					responseType: "blob",
				}
			);
			const url = URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement("a");
			link.href = url;
			link.download = this.$t("orderHistory.orders.prepayInvoice.fileName", {
				invoiceId,
			}) as string;
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			console.log(`Get prepay invoice failed. ${err}`);
		} finally {
			this.isLoadingPrepayPdf = false;
		}
	}

	private async getOrderConfirmationPdf(orderId: number) {
		try {
			this.isLoadingOrderPdf = true;
			const response = await httpClient().get(
				`order/getOrderConfirmationPdfFile?orderId=${orderId}`,
				{
					responseType: "blob",
				}
			);
			const url = URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement("a");
			link.href = url;
			link.download = this.$t("orderHistory.orders.orderConfirmation.fileName", {
				orderId,
			}) as string;
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			console.error(`Get order confirmation failed. ${err}`);
		} finally {
			this.isLoadingOrderPdf = false;
		}
	}

	private navigateToTicket(orderId: number) {
		this.$router.push({
			name: "CreateOrEditTicket",
			params: { orderId: orderId.toString() },
		});
	}

	private checkOrdersBottom() {
		let bottomOfWindow =
			window.innerHeight + window.pageYOffset >= document.body.offsetHeight - 2;
		if (bottomOfWindow && this.hasMoreToLoad && !this.isLoading) {
			this.loadOrders(false);
		}
	}

	private manuallyLoadMoreArticles() {
		if (this.hasMoreToLoad && !this.isLoading) {
			this.loadOrders(false);
		}
	}

	private async cancellationButton(orderId: number) {
		if (this.hasOpenCancellationRequest(orderId)) {
			this.$router.push({
				name: "CancellationConfirmation",
				params: { orderId: orderId.toString() },
			});
		} else {
			await this.checkCancellation(orderId);
		}
	}

	private async checkCancellation(orderId: number) {
		try {
			this.isLoadingCancellation = true;
			this.orderIdToCancel = orderId;
			const response = await httpClient().get(`Order/IsOrderTransmitted?orderId=${orderId}`);
			if (response.data) {
				this.$router.push({
					name: "RequestCancellation",
					params: {
						orderId: orderId.toString(),
						orderProcessedInMeantime: "false",
					},
				});
			} else {
				this.showCancellationConfirmationDialog = true;
			}
		} catch (err) {
			console.error(`Failed to check cancellation state. ${err}`);
		} finally {
			this.isLoadingCancellation = false;
		}
	}

	private async cancelOrder() {
		try {
			this.isLoadingCancellation = true;
			const response = await httpClient().post(
				`Order/CancelOrder?orderId=${this.orderIdToCancel}`
			);
			// FALSE if the order has started processing in the meantime
			if (response.data) {
				this.$store.dispatch(
					"setSnackbarText",
					this.$t("orderHistory.orders.cancellation.orderCancelledSuccess")
				);
				this.loadOrders(true); // Reload orders
			} else {
				this.$store.dispatch(
					"setSnackbarErrorText",
					this.$t("orderHistory.orders.cancellation.orderAlreadyProcessed")
				);
				this.$router.push({
					name: "RequestCancellation",
					params: {
						orderId: this.orderIdToCancel.toString(),
						orderProcessedInMeantime: "true",
					},
				});
			}
		} catch (err) {
			this.$store.dispatch(
				"setSnackbarErrorText",
				this.$t("orderHistory.orders.cancellation.cancelFailed")
			);
		} finally {
			this.isLoadingCancellation = false;
			this.showCancellationConfirmationDialog = false;
		}
	}

	private async editAddress(type: string, order: OrderTrackingDto) {
		try {
			let call = "";
			if (type === "invoice") {
				call = `Order/GetOrderInvoiceAddress?orderId=${order.orderId}`;
				this.currentEditAddressType = AddressType.CompletedOrderInvoice;
			} else if (type === "delivery") {
				call = `Order/GetOrderDeliveryAddress?orderId=${order.orderId}`;
				this.currentEditAddressType = AddressType.CompletedOrderDelivery;
			} else {
				throw "Invalid address type";
			}
			const response = await httpClient().get(call);
			let responseAddress: AddressDto = JSON.parse(JSON.stringify(response.data));
			if (type === "invoice" && responseAddress.eMail) {
				responseAddress.isBilledDigitally = responseAddress.eMail.length > 0;
			}
			this.currentEditAddress = responseAddress;
			this.currentEditAddressOrderId = order.orderId;
			this.editAddressDialog = true;
		} catch (err) {
			console.log(`Adresse konnte nicht geladen werden. ${err}`);
		}
	}
}
